/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { GET_FAILED_TEST_RESULTS_FOR_FFIEC031, GET_RULE_AUDIT_LOGS_FOR_FFIEC031 } from 'Apollo/Queries/Rules/FFIEC031Results';
import {
    showReportMenuVar, leftNavigationItemsVar, moduleIdVar, currentLeftMenuVar,
    tenantIdVar,
} from 'Apollo/config';
import { PageSizeOptionEnum, ModuleEnum } from 'constants';
import crossReportImg from 'assets/images/cross-report-black.svg';
import FFIEC031Results from '../components/FFIEC031Results';

const {
    FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE,
} = PageSizeOptionEnum;

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem', color: '#464646' }}>Data Reconciliation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-ticket-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem' }}>Rule Engine</span>,
    ],
    [
        <img
            key={1}
            src={crossReportImg}
            alt="Cross Report"
            style={{
                width: '30.84px', height: '25px', top: '100px',
            }}
        />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Cross Report Validation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-grow-revenue-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Trend Analytics</span>,
    ],
];

const FFIEC031ResultsContainer = () => {
    const ffiec031PageSizeOptions = [FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE];
    const location = useLocation();
    const history = useHistory();
    const [batchId, setBatchId] = useState(location.state.batchId);
    const [ruleId, setRuleId] = useState(location.state.ruleId);
    const [data, setData] = useState([]);
    const [ffiec031Pages, setPages] = useState(null);
    const [ffiec031PageSize, setPageSize] = useState(TEN);
    const [ffiec031PageNumber, setPageNumber] = useState(1);
    const tenantId = useReactiveVar(tenantIdVar);

    /* Apollo query to fetch audit logs */
    const auditLog = useQuery(GET_RULE_AUDIT_LOGS_FOR_FFIEC031, {
        variables: {
            input: {
                batchId: Number(batchId),
                ruleId: Number(ruleId),
                tenantId: Number(tenantId),
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Apollo query to fetch failed test data */
    const [getFailedTestsData, { data: { getFailedTestResultsForFFIEC031 = {} } = {} }] = useLazyQuery(GET_FAILED_TEST_RESULTS_FOR_FFIEC031, {
        variables: {
            input: {
                batchId: Number(batchId),
                ruleId: Number(ruleId),
                tenantId: Number(tenantId),
                pageSize: ffiec031PageSize,
                pageNumber: ffiec031PageNumber,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Calls getFailedTestsData() */
    const fetchData = useCallback(
        () => {
            getFailedTestsData();
        },
    );

    /* Method for side navigation */
    const onNavigate = () => {
        // sidebar navigation
    };

    /* Column configuration */
    const columns = [
        {
            Header: 'Row ID',
            accessor: 'rowId',
            minWidth: 150,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Error Message',
            accessor: 'errorMessage',
            minWidth: 230,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Error Data',
            accessor: 'errorData',
            minWidth: 200,
            resizable: true,
            sortable: true,
        },
    ];

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: (
                <span
                    className="a-breadcrumb-item-text"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.goBack();
                        }
                    }
                    role="presentation"
                >
                    FFIEC041 Diagnostic Results
                </span>
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">{`Rule #${ruleId} Results`}</span>,
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        currentLeftMenuVar(ModuleEnum.RULEENGINE);
    }, []);
    /* Side effect when location state updates */
    useEffect(() => {
        setBatchId(location.state.batchId);
        setRuleId(location.state.ruleId);
        showReportMenuVar(true);
    }, [location]);

    /* Side effect when getFailedTestResultsForFFIEC031 changes */
    useEffect(() => {
        if (getFailedTestResultsForFFIEC031 && getFailedTestResultsForFFIEC031.pagination !== null && Object.keys(getFailedTestResultsForFFIEC031).length > 0) {
            const totalPages = Math.ceil(getFailedTestResultsForFFIEC031.pagination.totalCount / ffiec031PageSize);
            setData(getFailedTestResultsForFFIEC031.testResults);
            setPages(totalPages);
        }
    }, [getFailedTestResultsForFFIEC031]);

    return (
        <FFIEC031Results
            breadcrumbItems={breadcrumbItems}
            onNavigate={onNavigate}
            criteria={getFailedTestResultsForFFIEC031 && getFailedTestResultsForFFIEC031.criteria}
            columns={columns}
            fetchData={fetchData}
            data={data}
            refetch={getFailedTestsData}
            auditLog={auditLog}
            pages={ffiec031Pages}
            setPageNumber={setPageNumber}
            pagesize={ffiec031PageSize}
            setPageSize={setPageSize}
            pageSizeOptions={ffiec031PageSizeOptions}
        />
    );
};

export default FFIEC031ResultsContainer;
